import { useMemo, useState, useEffect } from "react";

// ===== HELPERS =====
function fmtPunti(v) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  const s = (n >= 0 ? "+" : "") + n.toFixed(1);
  return s.replace(".", ",");
}
function fmtPpe(v) {
  if (v === null || v === undefined) return "—";
  return Number(v).toFixed(2).replace(".", ",");
}
function fmtEventi(v) {
  if (v === null || v === undefined) return "—";
  return Number(v).toLocaleString("it-IT");
}
function colPunti(v) {
  if (v === null || v === undefined) return "#64748B";
  if (v > 0) return "#16A34A";
  if (v < 0) return "#DC2626";
  return "#64748B";
}
function colVariazione(v) {
  if (v === null || v === undefined) return "#94A3B8";
  if (v > 0) return "#16A34A";
  if (v < 0) return "#DC2626";
  return "#94A3B8";
}
function freccia(v) {
  if (v === null || v === undefined) return "";
  if (v > 0) return "↗";
  if (v < 0) return "↘";
  return "→";
}
function variazionePct(curr, prev) {
  if (curr === null || curr === undefined || prev === null || prev === undefined) return null;
  if (prev === 0) return null;
  return ((curr - prev) / Math.abs(prev)) * 100;
}
function fmtVariazione(v) {
  if (v === null || v === undefined) return "";
  const r = Math.round(v);
  return (r >= 0 ? "+" : "") + r + "%";
}

// ===== UTILITY =====
function calcolaStagioni5(items) {
  const set = new Set();
  items.forEach(it => {
    Object.keys(it.dati_per_stagione || {}).forEach(st => set.add(st));
  });
  const tutte = [...set].sort();
  return tutte.slice(-5);
}
function kpiEventi(item, stag) {
  const d = item.dati_per_stagione?.[stag];
  if (!d) return null;
  return d.eventi ?? null;
}
function kpiPunti(item, stag) {
  const d = item.dati_per_stagione?.[stag];
  if (!d) return null;
  return d.punti ?? null;
}
function kpiPpe(item, stag) {
  const d = item.dati_per_stagione?.[stag];
  if (!d || !d.eventi) return null;
  return d.ppe ?? null;
}
function calcolaTotale(items, stagioni) {
  const tot = {};
  stagioni.forEach(stag => {
    let evSum = 0, ptSum = 0, hasData = false;
    items.forEach(it => {
      const d = it.dati_per_stagione?.[stag];
      if (d) {
        evSum += d.eventi || 0;
        ptSum += d.punti || 0;
        if (d.eventi) hasData = true;
      }
    });
    tot[stag] = hasData
      ? { eventi: evSum, punti: Math.round(ptSum*100)/100, ppe: evSum > 0 ? Math.round(ptSum/evSum*10000)/10000 : 0 }
      : null;
  });
  return tot;
}

// ===== CONFRONTI ARRICCHITI (D489 v174) =====
// Costanti di significatività (replica logica PortfolioHealth.js)
const N_STAGIONI_MEDIA = 3;
const SOGLIA_COMPLETAMENTO_PCT = 50;

// Calcola la media storica delle ultime N stagioni STRETTAMENTE precedenti allo
// stagioneFocus, escludendo la stagioneCorrente. Replica logica Health.
// Ritorna { punti_medio, ppe_medio, n_stagioni_usate, sufficiente }.
// sufficiente=true solo se trova almeno `n` stagioni precedenti.
function calcolaMediaStoricaRelativa(datiPerStagione, stagioneFocus, stagioneCorrente, n) {
  const vuoto = {
    punti_medio: null,
    ppe_medio: null,
    n_stagioni_usate: 0,
    sufficiente: false,
  };
  if (!datiPerStagione || !stagioneFocus) return vuoto;
  const tutte = Object.keys(datiPerStagione).sort();
  const precedenti = tutte.filter(s => s < stagioneFocus && s !== stagioneCorrente);
  const usate = precedenti.slice(-n);
  if (usate.length < n) {
    return { ...vuoto, n_stagioni_usate: usate.length };
  }
  let totEv = 0, totPt = 0;
  usate.forEach(s => {
    const d = datiPerStagione[s] || {};
    totEv += Number(d.eventi || 0);
    totPt += Number(d.punti || 0);
  });
  return {
    punti_medio: usate.length > 0 ? totPt / usate.length : null,
    ppe_medio: totEv > 0 ? totPt / totEv : null,
    n_stagioni_usate: usate.length,
    sufficiente: true,
  };
}

// ===== SCORING TREND (TASK 12.3 — Modello P1 pesato) =====
// Calcola un punteggio di trend per un sistema/strategia su un set di stagioni.
// Componenti:
//   - +1 per ogni stagione con Punti > 0
//   - Variazioni positive Punti pesate 1-2-3-4 (più recente pesa di più)
//   - Variazioni positive PPE pesate 1-2-3-4 (più recente pesa di più)
// Scaling: il punteggio è scalato sul massimo teorico realizzabile in base
// alle stagioni effettivamente disponibili per il sistema (per non penalizzare
// i sistemi giovani).
// Soglie (su scala 0-100%): >=60% ottimo, >=32% medio, <32% debole.
// Sistemi con meno di 2 stagioni → "Dati insufficienti" (nessun badge).
function calcolaScoreTrend(item, stagioni) {
  // Estrae le stagioni effettivamente popolate per questo item
  const stagioniValide = stagioni.filter(stag => {
    const d = item.dati_per_stagione?.[stag];
    return d && (d.eventi || d.eventi === 0) && d.eventi > 0;
  });
  const N = stagioniValide.length;

  if (N < 2) {
    return {
      score: null,
      scorePct: null,
      etichetta: null,
      colore: null,
      icona: null,
      testoEtichetta: "Dati insufficienti",
      stagioniValide: N,
      maxTeorico: 0,
    };
  }

  // Componente 1: stagioni positive
  let stagioniPositive = 0;
  stagioniValide.forEach(stag => {
    const pt = item.dati_per_stagione[stag].punti;
    if (pt !== null && pt !== undefined && pt > 0) stagioniPositive += 1;
  });

  // Componente 2 e 3: variazioni positive pesate
  // I pesi delle transizioni dipendono dalla posizione nella lista delle
  // stagioni valide. La transizione i (i=1..N-1) ha peso i: la più recente
  // pesa di più.
  let scorePunti = 0;
  let scorePpe = 0;
  let pesoTotalePunti = 0;  // massimo teorico componente Punti
  let pesoTotalePpe = 0;    // massimo teorico componente PPE
  for (let i = 1; i < N; i++) {
    const peso = i; // 1, 2, 3, 4...
    pesoTotalePunti += peso;
    pesoTotalePpe += peso;

    const stagCurr = stagioniValide[i];
    const stagPrev = stagioniValide[i-1];
    const ptCurr = item.dati_per_stagione[stagCurr].punti;
    const ptPrev = item.dati_per_stagione[stagPrev].punti;
    const ppCurr = item.dati_per_stagione[stagCurr].ppe;
    const ppPrev = item.dati_per_stagione[stagPrev].ppe;

    if (ptCurr !== null && ptCurr !== undefined && ptPrev !== null && ptPrev !== undefined && ptCurr > ptPrev) {
      scorePunti += peso;
    }
    if (ppCurr !== null && ppCurr !== undefined && ppPrev !== null && ppPrev !== undefined && ppCurr > ppPrev) {
      scorePpe += peso;
    }
  }

  // Score assoluto e massimo teorico realizzabile
  const score = stagioniPositive + scorePunti + scorePpe;
  const maxTeorico = N + pesoTotalePunti + pesoTotalePpe;
  const scorePct = maxTeorico > 0 ? (score / maxTeorico) * 100 : 0;

  // Etichettatura su scala 0-100%
  let etichetta, colore, icona, testoEtichetta;
  const scorePctR = Math.round(scorePct);
  if (scorePct >= 60) {
    etichetta = "ottima"; colore = "#16A34A"; icona = null; testoEtichetta = `Perf. Storica Ottima · ${scorePctR}%`;
  } else if (scorePct >= 32) {
    etichetta = "media"; colore = "#D97706"; icona = null; testoEtichetta = `Perf. Storica Media · ${scorePctR}%`;
  } else {
    etichetta = "debole"; colore = "#DC2626"; icona = null; testoEtichetta = `Perf. Storica Debole · ${scorePctR}%`;
  }

  return {
    score,
    scorePct,
    etichetta,
    colore,
    icona,
    testoEtichetta,
    stagioniValide: N,
    maxTeorico,
  };
}

// ===== COMPONENTE PRINCIPALE =====
export default function PortfolioTrendStorico({ items, tipo, onClickRiga }) {
  const stagioni = useMemo(() => calcolaStagioni5(items), [items]);
  // tutteLeStagioni: unione di tutte le stagioni presenti nei singoli items.
  // Usato per calcolare il TOTALE su storico completo (D489 v181), così la
  // 2ª freccia (vs media 3 stagioni chiuse) sulla riga TOTALE segue la stessa
  // regola dei singoli sistemi/strategie.
  const tutteLeStagioni = useMemo(() => {
    const set = new Set();
    items.forEach(it => {
      if (it.dati_per_stagione) {
        Object.keys(it.dati_per_stagione).forEach(s => set.add(s));
      }
    });
    return [...set].sort();
  }, [items]);
  const stagioneCorrente = stagioni.length > 0 ? stagioni[stagioni.length - 1] : null;

  // Stagione realmente in corso secondo il calendario leghe (backend).
  // Serve a distinguere "ultima colonna con dati" da "stagione in corso":
  // finche' la nuova stagione non ha ingressi, l'ultima colonna e' una
  // stagione GIA' CONCLUSA e non va trattata come in corso.
  // Se il backend non la fornisce (risposta vecchia), si ricade sul
  // comportamento precedente.
  const stagioneCorrenteReale = items[0]?.stagione_corrente_codice ?? null;
  const isStagioneInCorso = (stag) =>
    stagioneCorrenteReale !== null
      ? stag === stagioneCorrenteReale
      : stag === stagioneCorrente;
  const totale = useMemo(() => calcolaTotale(items, tutteLeStagioni), [items, tutteLeStagioni]);

  // ===== Lessico condizionale sistema/strategia (D489 v181) =====
  const isStrategia = tipo === "strategia";
  const labelSing = isStrategia ? "strategia" : "sistema";
  const labelPlur = isStrategia ? "strategie" : "sistemi";

  // ===== Calcolo score trend per ogni sistema (TASK 12.3) =====
  // Pre-calcolo: crea una mappa id → scoreInfo per accesso rapido
  // e per evitare doppio calcolo (ordinamento + rendering badge).
  const mapScore = useMemo(() => {
    const m = new Map();
    items.forEach(it => {
      m.set(it.id, calcolaScoreTrend(it, stagioni));
    });
    return m;
  }, [items, stagioni]);

  // === Stato toggle TOTALE (persistito su localStorage) ===
  const STORAGE_KEY = "trend-storico-mostra-totale";
  const [mostraTotale, setMostraTotale] = useState(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      return v === null ? true : v === "true";
    } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, String(mostraTotale)); } catch {}
  }, [mostraTotale]);

  // === Stato modal "Come si calcola" (TASK 12.3) ===
  const [modalAperto, setModalAperto] = useState(false);
  useEffect(() => {
    if (!modalAperto) return;
    const handler = (e) => { if (e.key === "Escape") setModalAperto(false); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [modalAperto]);

  // === Palette ===
  const COL_NAVY      = "#1F2A4A";
  const COL_BEIGE     = "#EFEEE9";
  const COL_BEIGE_HOT = "#E1DDD2";
  const COL_AZZURRO   = "#E6F1FB";
  const COL_GRIGIO_INT = "#F1F5F9"; // separatore interno tra sotto-righe
  const COL_GRIGIO_EXT = "#CBD5E1"; // separatore tra sistemi

  const eventiCorrente = stagioneCorrente && totale[stagioneCorrente]
    ? totale[stagioneCorrente].eventi
    : 0;

  if (!items.length) {
    return (
      <div style={{padding:40,textAlign:"center",color:"#94A3B8",fontSize:13}}>
        Nessun{tipo === "strategia" ? "a strategia attiva" : " sistema attivo"} trovato.
      </div>
    );
  }
  if (stagioni.length === 0) {
    return (
      <div style={{padding:40,textAlign:"center",color:"#94A3B8",fontSize:13}}>
        Nessuna stagione disponibile per il trend storico.
      </div>
    );
  }

  // ===== Componente cella valore con freccia + tooltip % =====
  const CellaValore = ({ valore, varPct, varPctMedia, tipo: tipoVal, bg, isTotale, isUltimaStagione, isUltimaSottoriga, isIngressi, tooltipTesto }) => {
    const borderBottom = isTotale && isUltimaSottoriga
      ? `2px solid ${COL_NAVY}`
      : isUltimaSottoriga
        ? `1px solid ${COL_GRIGIO_EXT}`
        : `1px solid ${COL_GRIGIO_INT}`;

    const borderTop = isTotale && tipoVal === "ingressi"
      ? `2px solid ${COL_NAVY}`
      : "none";

    const tdStyle = {
      padding:"7px 10px",
      textAlign:"center",
      fontSize: tipoVal === "ingressi" ? 12 : 13,
      fontFamily:"'IBM Plex Mono',monospace",
      background: bg,
      borderLeft: `1px solid ${COL_GRIGIO_INT}`,
      borderBottom,
      borderTop,
      verticalAlign:"middle",
    };

    if (valore === null || valore === undefined) {
      return <td style={{...tdStyle, color:"#CBD5E1"}} title={tooltipTesto || undefined}>—</td>;
    }

    // Formattazione valore principale
    let testoVal, colore;
    if (tipoVal === "ingressi") {
      testoVal = fmtEventi(valore);
      colore = "#64748B";
    } else if (tipoVal === "punti") {
      testoVal = fmtPunti(valore);
      colore = colPunti(valore);
    } else { // ppe
      testoVal = fmtPpe(valore);
      colore = "#1E293B";
    }

    // === D489 v174 — Doppia freccia: vs precedente (1ª) + vs media (2ª) ===
    // Slot vuoto = "–" grigio quando confronto non disponibile.
    const renderSlotFreccia = (v, marginLeft) => {
      if (v === null || v === undefined) {
        return (
          <span style={{
            marginLeft,
            fontSize: 13,
            color: "#CBD5E1",
            fontWeight: 400,
            cursor: "help",
          }}>
            –
          </span>
        );
      }
      return (
        <span style={{
          marginLeft,
          fontSize: 13,
          color: colVariazione(v),
          fontWeight: 700,
          cursor: "help",
        }}>
          {freccia(v)}
        </span>
      );
    };

    return (
      <td style={tdStyle} title={tooltipTesto || undefined}>
        <span style={{
          color: colore,
          fontWeight: tipoVal === "punti" ? 600 : (tipoVal === "ppe" ? 500 : 400),
        }}>
          {testoVal}
        </span>
        {tipoVal !== "ingressi" && (varPct !== null && varPct !== undefined || varPctMedia !== null && varPctMedia !== undefined) && (
          <>
            {renderSlotFreccia(varPct, 5)}
            {renderSlotFreccia(varPctMedia, 2)}
          </>
        )}
      </td>
    );
  };

  // ===== Rendering blocco sistema (3 sotto-righe) =====
  const renderBloccoSistema = (item, isTotale, isUltimo, scoreInfo) => {
    const bgRiga = isTotale ? COL_BEIGE : "#fff";
    const bgRigaCorr = isTotale ? COL_BEIGE_HOT : COL_AZZURRO;

    const kpiCellStyle = (sottoriga) => ({
      padding:"7px 10px",
      fontSize: 10,
      color: "#64748B",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      fontFamily: "'DM Sans',sans-serif",
      fontWeight: 500,
      borderLeft: `1px solid ${COL_GRIGIO_INT}`,
      borderBottom: isTotale && sottoriga === "ppe"
        ? `2px solid ${COL_NAVY}`
        : sottoriga === "ppe"
          ? `1px solid ${(isUltimo ? COL_GRIGIO_EXT : COL_GRIGIO_EXT)}`
          : `1px solid ${COL_GRIGIO_INT}`,
      borderTop: isTotale && sottoriga === "ingressi" ? `2px solid ${COL_NAVY}` : "none",
      background: bgRiga,
      verticalAlign: "middle",
      whiteSpace: "nowrap",
    });

    const sistemaCellStyle = {
      padding:"12px 12px",
      background: bgRiga,
      verticalAlign: "middle",
      borderTop: isTotale ? `2px solid ${COL_NAVY}` : "none",
      borderBottom: isTotale
        ? `2px solid ${COL_NAVY}`
        : `1px solid ${COL_GRIGIO_EXT}`,
    };

    // Genera le 3 sotto-righe per le stagioni
    const renderSotto = (sottoriga) => {
      return stagioni.map((stag, idx) => {
        const cellaCorr = stag === stagioneCorrente;
        const bg = cellaCorr ? bgRigaCorr : bgRiga;

        const ev = kpiEventi(item, stag);
        const pt = kpiPunti(item, stag);
        const pp = kpiPpe(item, stag);

        const stagPrev = idx > 0 ? stagioni[idx-1] : null;
        const ptPrev = stagPrev ? kpiPunti(item, stagPrev) : null;
        const ppPrev = stagPrev ? kpiPpe(item, stagPrev) : null;
        const varPt = idx > 0 ? variazionePct(pt, ptPrev) : null;
        const varPp = idx > 0 ? variazionePct(pp, ppPrev) : null;

        let val, varPct, tipoVal;
        if (sottoriga === "ingressi") { val = ev; varPct = null; tipoVal = "ingressi"; }
        else if (sottoriga === "punti") { val = pt; varPct = varPt; tipoVal = "punti"; }
        else { val = pp; varPct = varPp; tipoVal = "ppe"; }

        // === D489 v174 — Tooltip arricchito + dual-freccia ===
        // Calcola variazione vs precedente (varPctFinale) e vs media (varPctMedia)
        // per le 2 frecce, più tooltip multi-riga.
        // Regole significatività:
        //  - stagione corrente con completamento < SOGLIA → entrambe frecce neutralizzate
        //  - <N stagioni precedenti chiuse disponibili → freccia media neutralizzata
        let tooltipTesto = null;
        let varPctFinale = varPct;
        let varPctMedia = null;
        if (sottoriga !== "ingressi" && val !== null && val !== undefined) {
          const isStagCorrente = isStagioneInCorso(stag);
          const completamentoPct = items[0]?.completamento_pct_stagione_corrente ?? 100;
          const focusSignificativo = !isStagCorrente || completamentoPct >= SOGLIA_COMPLETAMENTO_PCT;

          const righe = [];

          // Intestazione per stagione corrente in corso
          if (isStagCorrente && completamentoPct < 100) {
            righe.push(`Stagione ${stag} — in corso al ${completamentoPct}%`);
          }

          if (!focusSignificativo) {
            righe.push(`Confronti significativi solo dal ${SOGLIA_COMPLETAMENTO_PCT}% di completamento`);
            // Blinda entrambe le frecce
            varPctFinale = null;
            varPctMedia = null;
          } else {
            // Riga 1: vs stagione precedente
            const valPrev = sottoriga === "punti" ? ptPrev : ppPrev;
            const varPctLocal = sottoriga === "punti" ? varPt : varPp;
            if (varPctLocal !== null && varPctLocal !== undefined && valPrev !== null && valPrev !== undefined) {
              const formatValPrev = sottoriga === "punti" ? fmtPunti(valPrev) : fmtPpe(valPrev);
              righe.push(`${fmtVariazione(varPctLocal)} vs stagione precedente (${formatValPrev})`);
            } else {
              righe.push(`— vs stagione precedente (non disponibile per quest${isStrategia ? "a strategia" : "o sistema"})`);
            }

            // Riga 2: vs media N stagioni precedenti
            const med = calcolaMediaStoricaRelativa(
              item.dati_per_stagione,
              stag,
              stagioneCorrente,
              N_STAGIONI_MEDIA
            );
            if (med.sufficiente) {
              const valMed = sottoriga === "punti" ? med.punti_medio : med.ppe_medio;
              if (valMed !== null && valMed !== 0) {
                varPctMedia = ((val - valMed) / Math.abs(valMed)) * 100;
                const formatValMed = sottoriga === "punti" ? fmtPunti(valMed) : fmtPpe(valMed);
                righe.push(`${fmtVariazione(varPctMedia)} vs media ${N_STAGIONI_MEDIA} stagioni precedenti (${formatValMed})`);
              } else {
                righe.push(`— vs media ${N_STAGIONI_MEDIA} stagioni precedenti`);
              }
            } else {
              righe.push(`— vs media ${N_STAGIONI_MEDIA} stagioni precedenti (servono ≥${N_STAGIONI_MEDIA} stagioni chiuse)`);
            }
          }

          if (righe.length > 0) {
            tooltipTesto = righe.join("\n");
          }
        }

        return (
          <CellaValore
            key={`${stag}-${sottoriga}`}
            valore={val}
            varPct={varPctFinale}
            varPctMedia={varPctMedia}
            tipo={tipoVal}
            bg={bg}
            isTotale={isTotale}
            isUltimaStagione={idx === stagioni.length - 1}
            isUltimaSottoriga={sottoriga === "ppe"}
            isIngressi={sottoriga === "ingressi"}
            tooltipTesto={tooltipTesto}
          />
        );
      });
    };

    return (
      <tbody key={item.id || "totale"}
        onClick={() => !isTotale && onClickRiga && onClickRiga(item)}
        style={{cursor: (!isTotale && onClickRiga) ? "pointer" : "default"}}
      >
        {/* Sotto-riga 1: Ingressi */}
        <tr>
          <td rowSpan={3} style={sistemaCellStyle}>
            {isTotale ? (
              <div style={{
                fontWeight: 700,
                fontSize: 13,
                color: COL_NAVY,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}>
                TOTALE
              </div>
            ) : (
              <>
                <div style={{display:"flex", alignItems:"center", gap:6, flexWrap:"wrap"}}>
                  <span style={{fontWeight:700, fontSize:13, color:"#1E293B"}}>
                    {item.nome}
                  </span>
                  {scoreInfo && (
                    scoreInfo.etichetta ? (
                      <span
                        title={`Performance storica: valutazione complessiva ${isStrategia ? "della strategia" : "del sistema"} sulle ultime stagioni. Score ${Math.round(scoreInfo.scorePct)}/100, categoria ${scoreInfo.etichetta}. Per i dettagli del calcolo vedi "Come si calcola" in legenda.`}
                        style={{
                          fontSize: 11,
                          padding: "2px 7px",
                          borderRadius: 10,
                          background: scoreInfo.colore + "1A",  // tinta opaca dello stesso colore
                          color: scoreInfo.colore,
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          cursor: "help",
                          fontFamily: "'DM Sans',sans-serif",
                          letterSpacing: 0,
                          textTransform: "none",
                        }}
                      >
                        {scoreInfo.testoEtichetta}
                      </span>
                    ) : (
                      <span
                        title="Servono almeno 2 stagioni con dati per valutare il trend"
                        style={{
                          fontSize: 11,
                          padding: "2px 7px",
                          borderRadius: 10,
                          background: "#F1F5F9",
                          color: "#64748B",
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                          cursor: "help",
                          fontFamily: "'DM Sans',sans-serif",
                          letterSpacing: 0,
                          textTransform: "none",
                          fontStyle: "italic",
                        }}
                      >
                        Dati insufficienti
                      </span>
                    )
                  )}
                </div>
                {tipo === "sistema" && item.mercato && (
                  <div style={{fontSize:11, color:"#94A3B8", marginTop:2, fontWeight:400}}>
                    {item.mercato}
                  </div>
                )}
              </>
            )}
          </td>
          <td style={{...kpiCellStyle("ingressi"), cursor:"help"}} title="Ingressi: numero di ingressi a mercato nella stagione.">Ingressi</td>
          {renderSotto("ingressi")}
        </tr>
        {/* Sotto-riga 2: Punti */}
        <tr>
          <td style={{...kpiCellStyle("punti"), cursor:"help"}} title={`Punti: P/L totale della stagione (somma dei coefficienti P/L generati ${isStrategia ? "dalla strategia" : "dal sistema"}).`}>Punti</td>
          {renderSotto("punti")}
        </tr>
        {/* Sotto-riga 3: PPE */}
        <tr>
          <td style={{...kpiCellStyle("ppe"), cursor:"help"}} title="PPE (Punti Per Evento): redditività media per singolo evento. Calcolata come Punti / Ingressi.">PPE</td>
          {renderSotto("ppe")}
        </tr>
      </tbody>
    );
  };

  // ===== Ordinamento per score decrescente (TASK 12.3) =====
  // Sistemi con score null (Dati insufficienti) finiscono in fondo.
  // A parità di score: ordine alfabetico per nome.
  const itemsOrd = [...items].sort((a,b) => {
    const sa = mapScore.get(a.id);
    const sb = mapScore.get(b.id);
    const va = sa && sa.score !== null ? sa.score : -Infinity;
    const vb = sb && sb.score !== null ? sb.score : -Infinity;
    if (vb !== va) return vb - va;
    return (a.nome||"").localeCompare(b.nome||"");
  });

  const totaleAsItem = {
    id: "__totale__",
    nome: "TOTALE",
    dati_per_stagione: Object.fromEntries(
      Object.entries(totale).filter(([,v]) => v !== null && v !== undefined)
    )
  };

  const thNavy = {
    background:COL_NAVY, color:"#fff",
    fontSize:11, fontWeight:700,
    letterSpacing:"0.05em", textTransform:"uppercase",
    padding:"10px 8px",
    whiteSpace:"nowrap",
    fontFamily:"'DM Sans',sans-serif",
  };

  return (
    <div>

      {/* === Toggle "Mostra riga totale" === */}
      <div style={{
        padding:"10px 4px 12px",
        display:"flex", alignItems:"center", gap:8,
      }}>
        <label style={{
          display:"flex", alignItems:"center", gap:8,
          cursor:"pointer", fontSize:13, color:"#64748B",
          userSelect:"none",
          fontFamily:"'DM Sans',sans-serif",
        }}>
          <input
            type="checkbox"
            checked={mostraTotale}
            onChange={(e) => setMostraTotale(e.target.checked)}
            style={{width:16, height:16, cursor:"pointer", accentColor:COL_NAVY}}
          />
          <span>Mostra riga totale</span>
        </label>
      </div>

      {/* === Tabella === */}
      <div style={{overflowX:"auto"}}>
        <table style={{width:"100%", borderCollapse:"separate", borderSpacing:0,
          tableLayout:"fixed"}}>
          <colgroup>
            <col style={{width:220}}/>
            <col style={{width:90}}/>
            {stagioni.map(stag => <col key={stag}/>)}
          </colgroup>
          <thead>
            <tr>
              <th colSpan={2} style={{...thNavy, textAlign:"left"}}>
                {tipo === "strategia" ? "STRATEGIA" : "SISTEMA"}
              </th>
              {stagioni.map((stag, idx) => {
                const corr = stag === stagioneCorrente;
                const completamentoPct = items[0]?.completamento_pct_stagione_corrente ?? 100;
                const mostraPct = isStagioneInCorso(stag) && completamentoPct < 100;
                return (
                  <th key={stag} style={{
                    ...thNavy,
                    textAlign:"center",
                    padding:"10px 8px",
                    background: corr ? "#2A3A5F" : COL_NAVY,
                    borderLeft: idx > 0 ? "1px solid rgba(255,255,255,0.18)" : "none",
                  }}>
                    {stag}{mostraPct ? ` (${completamentoPct}%)` : ""}
                  </th>
                );
              })}
            </tr>
          </thead>

          {/* TOTALE (condizionale) */}
          {mostraTotale && renderBloccoSistema(totaleAsItem, true, false, null)}

          {/* Sistemi/Strategie */}
          {itemsOrd.map((item, idx) => renderBloccoSistema(item, false, idx === itemsOrd.length - 1, mapScore.get(item.id)))}
        </table>
      </div>

      {/* === Legenda — 2 righe etichettate (D489 v181: separazione Performance storica vs Trend stagionale) === */}
      <div style={{
        padding: "12px 16px",
        background: "#FAFBFC",
        borderTop: "1px solid #E8E8E4",
        fontSize: 11.5,
        color: "#64748B",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        fontFamily: "'DM Sans',sans-serif",
      }}>
        {/* Riga 1: PERFORMANCE STORICA */}
        <div style={{display: "flex", alignItems: "center", flexWrap: "wrap", gap: 14}}>
          <span style={{fontWeight: 600, color: "#475569", minWidth: 160, letterSpacing: 0.4, fontSize: 11}}>
            PERFORMANCE STORICA
          </span>
          <span style={{display:"inline-flex", alignItems:"center", gap:5}}>
            <span style={{color:"#16A34A", fontSize:12, lineHeight:1}}>●</span> Ottima
          </span>
          <span style={{display:"inline-flex", alignItems:"center", gap:5}}>
            <span style={{color:"#D97706", fontSize:12, lineHeight:1}}>●</span> Media
          </span>
          <span style={{display:"inline-flex", alignItems:"center", gap:5}}>
            <span style={{color:"#DC2626", fontSize:12, lineHeight:1}}>●</span> Debole
          </span>
          <span style={{color:"#94A3B8", fontStyle:"italic"}}>
            score 0-100% · dati insufficienti se meno di 2 stagioni
          </span>
          <button
            type="button"
            onClick={() => setModalAperto(true)}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              color: "#3B5378",
              fontSize: 11.5,
              fontFamily: "'DM Sans',sans-serif",
              textDecoration: "underline",
              textDecorationStyle: "dotted",
              textUnderlineOffset: 3,
            }}
          >
            Come si calcola
          </button>
        </div>
        {/* Riga 2: TREND STAGIONALE */}
        <div style={{display: "flex", alignItems: "center", flexWrap: "wrap", gap: 14}}>
          <span style={{fontWeight: 600, color: "#475569", minWidth: 160, letterSpacing: 0.4, fontSize: 11}}>
            TREND STAGIONALE
          </span>
          <span style={{display:"inline-flex", alignItems:"center", gap:5}}>
            <span style={{color:"#16A34A", fontWeight:700}}>↗</span> migliora
          </span>
          <span style={{display:"inline-flex", alignItems:"center", gap:5}}>
            <span style={{color:"#DC2626", fontWeight:700}}>↘</span> peggiora
          </span>
          <span style={{display:"inline-flex", alignItems:"center", gap:5}}>
            <span style={{color:"#CBD5E1"}}>–</span> non disponibile
          </span>
          <span style={{color:"#94A3B8", fontStyle:"italic"}}>
            (Prima freccia: confronto con stagione precedente - Seconda freccia: confronto con media ultime {N_STAGIONI_MEDIA} stagioni concluse)
          </span>
        </div>
      </div>

      {/* === Footer === */}
      <div style={{padding:"10px 16px", background:"#F8FAFC",
        borderTop:"1px solid #E8E8E4", fontSize:11, color:"#94A3B8"}}>
        {items.length} {tipo === "strategia"
          ? (items.length === 1 ? "strategia attiva" : "strategie attive")
          : (items.length === 1 ? "sistema attivo" : "sistemi attivi")
        } · {eventiCorrente.toLocaleString("it-IT")} eventi totali nella stagione corrente
      </div>

      {/* === Modal "Come si calcola il punteggio di trend" (TASK 12.3) === */}
      {modalAperto && (
        <div
          onClick={() => setModalAperto(false)}
          style={{
            position: "fixed",
            top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(15, 23, 42, 0.55)",
            zIndex: 9000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 12,
              maxWidth: 720,
              width: "100%",
              maxHeight: "90vh",
              overflowY: "auto",
              boxShadow: "0 20px 60px rgba(15, 23, 42, 0.3)",
              fontFamily: "'DM Sans',sans-serif",
            }}
          >
            {/* Header modal */}
            <div style={{
              padding: "18px 24px",
              borderBottom: "1px solid #E8E8E4",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: "#F8FAFC",
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
            }}>
              <div style={{
                fontSize: 16,
                fontWeight: 700,
                color: "#1F2A4A",
                letterSpacing: "0.02em",
              }}>
                Come si calcola la Performance storica
              </div>
              <button
                onClick={() => setModalAperto(false)}
                aria-label="Chiudi"
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: 22,
                  color: "#64748B",
                  padding: "0 4px",
                  lineHeight: 1,
                }}
              >×</button>
            </div>

            {/* Body modal */}
            <div style={{padding: "20px 24px", fontSize: 13.5, color: "#1E293B", lineHeight: 1.55}}>

              <div style={{marginBottom: 16, color: "#475569"}}>
                Il punteggio di Performance storica valuta quanto {isStrategia ? "una strategia" : "un sistema"} sta migliorando nel tempo. Si compone di <b>tre ingredienti</b>:
              </div>

              <ol style={{margin: "0 0 18px 0", paddingLeft: 22, color: "#1E293B"}}>
                <li style={{marginBottom: 6}}>
                  <b>Stagioni positive</b>: +1 punto per ogni stagione con Punti maggiori di zero.
                </li>
                <li style={{marginBottom: 6}}>
                  <b>Variazioni Punti</b>: per ogni passaggio in cui i Punti migliorano rispetto alla stagione precedente, si aggiunge un peso. Le stagioni più recenti pesano di più (pesi 1, 2, 3, 4 dalla più vecchia alla più recente).
                </li>
                <li style={{marginBottom: 6}}>
                  <b>Variazioni PPE</b>: stessa logica della componente 2, applicata al PPE.
                </li>
              </ol>

              {/* Esempio numerico */}
              <div style={{
                background: "#F8FAFC",
                border: "1px solid #E2E8F0",
                borderRadius: 8,
                padding: "16px 18px",
                marginBottom: 18,
              }}>
                <div style={{fontWeight: 700, fontSize: 14, color: "#1F2A4A", marginBottom: 10}}>
                  Esempio pratico
                </div>
                <div style={{marginBottom: 12, color: "#475569"}}>
                  Immaginiamo {isStrategia ? "una strategia" : "un sistema"} con queste 5 stagioni di storico:
                </div>

                <table style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontFamily: "'IBM Plex Mono',monospace",
                  fontSize: 12.5,
                  marginBottom: 14,
                }}>
                  <thead>
                    <tr style={{background: "#1F2A4A", color: "#fff"}}>
                      <th style={{padding: "6px 10px", textAlign: "left", fontWeight: 600}}>Stagione</th>
                      <th style={{padding: "6px 10px", textAlign: "right", fontWeight: 600}}>Punti</th>
                      <th style={{padding: "6px 10px", textAlign: "right", fontWeight: 600}}>PPE</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td style={{padding:"5px 10px", borderBottom:"1px solid #E2E8F0"}}>21-22</td><td style={{padding:"5px 10px", textAlign:"right", color:"#16A34A", borderBottom:"1px solid #E2E8F0"}}>+5,0</td><td style={{padding:"5px 10px", textAlign:"right", borderBottom:"1px solid #E2E8F0"}}>+0,10</td></tr>
                    <tr><td style={{padding:"5px 10px", borderBottom:"1px solid #E2E8F0"}}>22-23</td><td style={{padding:"5px 10px", textAlign:"right", color:"#DC2626", borderBottom:"1px solid #E2E8F0"}}>−2,0</td><td style={{padding:"5px 10px", textAlign:"right", borderBottom:"1px solid #E2E8F0"}}>−0,05</td></tr>
                    <tr><td style={{padding:"5px 10px", borderBottom:"1px solid #E2E8F0"}}>23-24</td><td style={{padding:"5px 10px", textAlign:"right", color:"#16A34A", borderBottom:"1px solid #E2E8F0"}}>+8,0</td><td style={{padding:"5px 10px", textAlign:"right", borderBottom:"1px solid #E2E8F0"}}>+0,15</td></tr>
                    <tr><td style={{padding:"5px 10px", borderBottom:"1px solid #E2E8F0"}}>24-25</td><td style={{padding:"5px 10px", textAlign:"right", color:"#16A34A", borderBottom:"1px solid #E2E8F0"}}>+12,0</td><td style={{padding:"5px 10px", textAlign:"right", borderBottom:"1px solid #E2E8F0"}}>+0,20</td></tr>
                    <tr><td style={{padding:"5px 10px"}}>25-26</td><td style={{padding:"5px 10px", textAlign:"right", color:"#16A34A"}}>+15,0</td><td style={{padding:"5px 10px", textAlign:"right"}}>+0,22</td></tr>
                  </tbody>
                </table>

                <div style={{marginBottom: 8, fontWeight: 600, color: "#1F2A4A"}}>
                  Punteggio guadagnato {isStrategia ? "dalla strategia" : "dal sistema"}:
                </div>
                <ul style={{margin: "0 0 12px 0", paddingLeft: 22, color: "#1E293B"}}>
                  <li><b>Stagioni positive</b>: 4 su 5 (tutte tranne 22-23) → <b>+4</b></li>
                  <li>
                    <b>Variazioni Punti crescenti</b>:
                    <ul style={{paddingLeft: 20, marginTop: 4, color: "#475569"}}>
                      <li>21-22 → 22-23: peggiora → 0</li>
                      <li>22-23 → 23-24: migliora → peso 2</li>
                      <li>23-24 → 24-25: migliora → peso 3</li>
                      <li>24-25 → 25-26: migliora → peso 4</li>
                      <li style={{color: "#1E293B", marginTop: 2}}>Totale: <b>+9</b></li>
                    </ul>
                  </li>
                  <li style={{marginTop: 6}}>
                    <b>Variazioni PPE crescenti</b>: stessa logica → <b>+9</b>
                  </li>
                </ul>

                <div style={{
                  background: "#fff",
                  border: "1px solid #CBD5E1",
                  borderRadius: 6,
                  padding: "10px 14px",
                  marginBottom: 14,
                  fontFamily: "'IBM Plex Mono',monospace",
                  fontSize: 13,
                  color: "#1F2A4A",
                }}>
                  Punteggio = 4 + 9 + 9 = <b>22</b>
                </div>

                <div style={{marginBottom: 8, fontWeight: 600, color: "#1F2A4A"}}>
                  Massimo possibile (se {isStrategia ? "la strategia fosse stata perfetta" : "il sistema fosse stato perfetto"}):
                </div>
                <ul style={{margin: "0 0 12px 0", paddingLeft: 22, color: "#1E293B"}}>
                  <li>Stagioni positive: max 5</li>
                  <li>Variazioni Punti: max 1+2+3+4 = 10</li>
                  <li>Variazioni PPE: max 1+2+3+4 = 10</li>
                </ul>

                <div style={{
                  background: "#fff",
                  border: "1px solid #CBD5E1",
                  borderRadius: 6,
                  padding: "10px 14px",
                  marginBottom: 14,
                  fontFamily: "'IBM Plex Mono',monospace",
                  fontSize: 13,
                  color: "#1F2A4A",
                }}>
                  Massimo = 5 + 10 + 10 = <b>25</b>
                </div>

                <div style={{
                  background: "#16A34A1A",
                  border: "1px solid #16A34A",
                  borderRadius: 6,
                  padding: "12px 14px",
                  fontFamily: "'IBM Plex Mono',monospace",
                  fontSize: 14,
                  color: "#15803D",
                  textAlign: "center",
                  fontWeight: 600,
                }}>
                  Percentuale = 22 / 25 = <b>88%</b> → Performance ottima
                </div>
              </div>

              {/* Soglie */}
              <div style={{
                fontWeight: 700,
                fontSize: 14,
                color: "#1F2A4A",
                marginBottom: 10,
              }}>
                Soglie delle etichette
              </div>
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 10,
                marginBottom: 18,
              }}>
                <div style={{
                  background: "#16A34A1A",
                  border: "1px solid #16A34A",
                  borderRadius: 8,
                  padding: "10px 12px",
                  textAlign: "center",
                }}>
                  <div style={{fontWeight: 600, color: "#16A34A", marginBottom: 2}}>Performance ottima</div>
                  <div style={{fontSize: 12, color: "#1E293B"}}>≥ 60%</div>
                </div>
                <div style={{
                  background: "#D976061A",
                  border: "1px solid #D97706",
                  borderRadius: 8,
                  padding: "10px 12px",
                  textAlign: "center",
                }}>
                  <div style={{fontWeight: 600, color: "#D97706", marginBottom: 2}}>Performance media</div>
                  <div style={{fontSize: 12, color: "#1E293B"}}>32% – 59%</div>
                </div>
                <div style={{
                  background: "#DC26261A",
                  border: "1px solid #DC2626",
                  borderRadius: 8,
                  padding: "10px 12px",
                  textAlign: "center",
                }}>
                  <div style={{fontWeight: 600, color: "#DC2626", marginBottom: 2}}>Performance debole</div>
                  <div style={{fontSize: 12, color: "#1E293B"}}>&lt; 32%</div>
                </div>
              </div>

              {/* Nota sistemi giovani */}
              <div style={{
                fontSize: 12.5,
                color: "#64748B",
                fontStyle: "italic",
                paddingTop: 12,
                borderTop: "1px solid #E2E8F0",
              }}>
                {isStrategia
                  ? <>Le strategie con meno di 2 stagioni di storico non hanno abbastanza dati per essere valutate: appaiono con l'etichetta <b>"Dati insufficienti"</b>. Per le strategie più giovani il massimo possibile viene calcolato sulle sole stagioni disponibili, così strategie anziane e strategie giovani restano confrontabili.</>
                  : <>I sistemi con meno di 2 stagioni di storico non hanno abbastanza dati per essere valutati: appaiono con l'etichetta <b>"Dati insufficienti"</b>. Per i sistemi più giovani il massimo possibile viene calcolato sulle sole stagioni disponibili, così sistemi anziani e sistemi giovani restano confrontabili.</>
                }
              </div>

            </div>
          </div>
        </div>
      )}

    </div>
  );
}
